import { USE_MOCKS, api, descargarArchivo, mockDelay } from "@/lib/api"
import type { ReviewItem } from "@/lib/types"
import type {
  ApproveRow,
  BulkApproveResult,
  BulkApproveRow,
  CorrectRow,
  CostCenterLookupRow,
  MetricasRow,
  ReviewRow,
} from "@/routes/review/mapping"
import {
  approveFromApi,
  approveToApi,
  bulkApproveFromApi,
  bulkApproveToApi,
  correctionFromApi,
  correctionToApi,
  filtrarEnCliente,
  reviewItemFromApi,
} from "@/routes/review/mapping"

/**
 * Cola de revision de clasificacion. Modulo `classify` del backend.
 *
 *   GET   /api/classify/review          ?limite   (el tenant va en X-Tenant-Id)
 *   POST  /api/classify/{document_id}/approve    { nota? }
 *   POST  /api/classify/{document_id}/correct    { cuenta_local, centro_costo?, motivo, aprender }
 *   POST  /api/classify/bulk-approve             { document_ids, motivo }
 *
 * Toda correccion escribe la memoria de proveedor (party -> cuenta), y aprobar
 * tambien: por eso la cola se achica sola con el uso.
 *
 * Este archivo NO traduce: decide ruta y verbo y delega la forma del cuerpo y de
 * la respuesta a `mapping.ts`, igual que en `masters`. El modo `USE_MOCKS` sigue
 * intacto y devuelve los tipos de la SPA sin pasar por el mapeo, que es lo que lo
 * hace util como control.
 */

export interface ReviewFilters {
  source?: string
  channel?: string
  maxConfidence?: number
  query?: string
  onlyPending?: boolean
}

export interface CorrectionPayload {
  accountCode: string
  costCenter?: string
  taxTreatment: string
  /** Obligatorio: queda en el audit log. */
  reason: string
}

const MOCK_ITEMS: ReviewItem[] = [
  {
    id: "rv-001",
    docType: "01",
    serie: "F001",
    numero: "0004821",
    issueDate: "2026-07-03",
    supplierRuc: "20512345678",
    supplierName: "Servicios Cloud Andina SAC",
    currency: "PEN",
    net: 4200,
    igv: 756,
    total: 4956,
    accountCode: "63911",
    accountName: "Otros servicios prestados por terceros",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-TEC",
    taxTreatment: "Credito fiscal integro",
    source: "llm",
    confidence: 0.62,
    rationale:
      "Proveedor nuevo. La descripcion 'suscripcion mensual infraestructura' no coincide con ninguna regla; se propone 63911 por analogia con proveedores de hosting del mismo giro.",
    pipelineRunId: "run-2026-07-0012",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "completo",

    flags: ["proveedor_nuevo"],
  },
  {
    id: "rv-002",
    docType: "01",
    serie: "F002",
    numero: "0000912",
    issueDate: "2026-07-04",
    supplierRuc: "20100047218",
    supplierName: "Transportes Vega SAC",
    currency: "PEN",
    net: 8500,
    igv: 1530,
    total: 10030,
    detractionCode: "027",
    detractionRate: 0.04,
    accountCode: "63111",
    accountName: "Transporte de carga",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-OPE",
    taxTreatment: "Detraccion 4% (cod. 027) — credito fiscal condicionado al deposito",
    source: "regla",
    confidence: 0.98,
    rationale:
      "Regla PE-DET-027: servicio de transporte de bienes por via terrestre sobre S/ 400 activa detraccion del 4%. Cuenta tomada del rulepack.",
    pipelineRunId: "run-2026-07-0012",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "completo",

    flags: ["detraccion"],
  },
  {
    id: "rv-003",
    docType: "01",
    serie: "E001",
    numero: "0000233",
    issueDate: "2026-07-05",
    supplierRuc: "20601234567",
    supplierName: "Estudio Legal Paredes & Asoc.",
    currency: "USD",
    net: 3000,
    igv: 540,
    total: 3540,
    accountCode: "63241",
    accountName: "Asesoria legal",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-ADM",
    taxTreatment: "Credito fiscal integro",
    source: "memoria",
    confidence: 0.94,
    rationale:
      "Memoria de proveedor: 11 comprobantes previos del RUC 20601234567 clasificados en 63241 y confirmados por un humano.",
    pipelineRunId: "run-2026-07-0012",
    status: "pendiente",
    ingestChannel: "correo",
    sustento: "sin_representacion",

    flags: ["moneda_extranjera"],
  },
  {
    id: "rv-004",
    docType: "01",
    serie: "F100",
    numero: "0001177",
    issueDate: "2026-07-07",
    supplierRuc: "20555512345",
    supplierName: "Restaurante La Bahia EIRL",
    currency: "PEN",
    net: 980,
    igv: 176.4,
    total: 1156.4,
    accountCode: "63711",
    accountName: "Gastos de representacion",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-COM",
    taxTreatment: "Sujeto a limite: 0.5% ingresos brutos / 40 UIT (adicion en TAX)",
    source: "llm",
    confidence: 0.71,
    rationale:
      "Consumo en restaurante con nota 'almuerzo cliente Nexo'. Podria ser representacion (63711) o movilidad del personal (63151). Se propone representacion por la glosa.",
    pipelineRunId: "run-2026-07-0013",
    status: "pendiente",
    ingestChannel: "correo",
    sustento: "completo",

    flags: ["limite_tributario", "criterio"],
  },
  {
    id: "rv-005",
    docType: "01",
    serie: "F001",
    numero: "0009011",
    issueDate: "2026-07-08",
    supplierRuc: "20131312955",
    supplierName: "Municipalidad de San Isidro",
    currency: "PEN",
    net: 640,
    igv: 0,
    total: 640,
    accountCode: "65911",
    accountName: "Sanciones administrativas",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-ADM",
    taxTreatment: "No deducible (art. 44 inc. c LIR) — adicion",
    source: "regla",
    confidence: 0.99,
    rationale:
      "Regla TR-014 (multas): statutory incluye, tax adiciona, management excluye del P&L por no ser operativo.",
    pipelineRunId: "run-2026-07-0013",
    status: "pendiente",
    ingestChannel: "manual",
    sustento: "sin_comprobante_fiscal",

    flags: ["no_deducible", "regla_tratamiento"],
  },
  {
    id: "rv-006",
    docType: "03",
    serie: "B001",
    numero: "0023344",
    issueDate: "2026-07-09",
    supplierRuc: "10456789012",
    supplierName: "Libreria Central",
    currency: "PEN",
    net: 118,
    igv: 21.24,
    total: 139.24,
    accountCode: "60311",
    accountName: "Suministros de oficina",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-ADM",
    taxTreatment: "Boleta: sin credito fiscal",
    source: "memoria",
    confidence: 0.88,
    rationale: "Memoria de proveedor: 4 comprobantes previos en 60311.",
    pipelineRunId: "run-2026-07-0013",
    status: "pendiente",
    ingestChannel: "manual",
    sustento: "completo",

    flags: ["sin_credito_fiscal"],
  },
  {
    id: "rv-007",
    docType: "07",
    serie: "FC01",
    numero: "0000045",
    issueDate: "2026-07-11",
    supplierRuc: "20512345678",
    supplierName: "Servicios Cloud Andina SAC",
    currency: "PEN",
    net: -420,
    igv: -75.6,
    total: -495.6,
    accountCode: "63911",
    accountName: "Otros servicios prestados por terceros",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-TEC",
    taxTreatment: "Nota de credito: reversa credito fiscal",
    source: "regla",
    confidence: 0.97,
    rationale: "Tipo 07 vinculado a F001-0004821: se replica la cuenta del documento de origen.",
    pipelineRunId: "run-2026-07-0014",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "completo",

    flags: ["nota_credito"],
  },
  {
    id: "rv-008",
    docType: "01",
    serie: "F003",
    numero: "0000781",
    issueDate: "2026-07-12",
    supplierRuc: "20478899001",
    supplierName: "Consultora Talento Plus SAC",
    currency: "PEN",
    net: 15000,
    igv: 2700,
    total: 17700,
    detractionCode: "022",
    detractionRate: 0.12,
    accountCode: "63271",
    accountName: "Asesoria empresarial",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-OPE",
    taxTreatment: "Detraccion 12% (cod. 022)",
    source: "llm",
    confidence: 0.55,
    rationale:
      "Glosa ambigua: 'servicios de acompanamiento'. Puede ser asesoria empresarial (63271) o servicio de personal tercerizado, que cambiaria la naturaleza a costo de ventas.",
    pipelineRunId: "run-2026-07-0014",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "sin_representacion",

    flags: ["criterio", "impacto_margen"],
  },
  {
    id: "rv-009",
    docType: "01",
    serie: "F001",
    numero: "0000158",
    issueDate: "2026-07-15",
    supplierRuc: "20334455667",
    supplierName: "Equipos Tecnologicos del Sur SAC",
    currency: "PEN",
    net: 12400,
    igv: 2232,
    total: 14632,
    accountCode: "33611",
    accountName: "Equipos de computo",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-TEC",
    taxTreatment: "Activo fijo: depreciacion NIC 16 vs. tasa maxima SUNAT 25%",
    source: "llm",
    confidence: 0.68,
    rationale:
      "Importe y descripcion (8 laptops) sugieren activo fijo, no gasto. Requiere confirmar politica de capitalizacion del cliente.",
    pipelineRunId: "run-2026-07-0015",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "completo",

    flags: ["capex", "criterio"],
  },
  {
    id: "rv-010",
    docType: "01",
    serie: "F011",
    numero: "0003390",
    issueDate: "2026-07-18",
    supplierRuc: "20100128056",
    supplierName: "Energia del Peru SA",
    currency: "PEN",
    net: 1830,
    igv: 329.4,
    total: 2159.4,
    accountCode: "63611",
    accountName: "Energia electrica",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-ADM",
    taxTreatment: "Credito fiscal integro",
    source: "memoria",
    confidence: 0.96,
    rationale: "Memoria de proveedor: 24 comprobantes previos en 63611.",
    pipelineRunId: "run-2026-07-0015",
    status: "pendiente",
    ingestChannel: "sunat",
    sustento: "sin_sustento",

    flags: [],
  },
  {
    id: "rv-011",
    docType: "01",
    serie: "F007",
    numero: "0000064",
    issueDate: "2026-07-19",
    supplierRuc: "20609988776",
    supplierName: "Marketing Delta SAC",
    currency: "PEN",
    net: 6200,
    igv: 1116,
    total: 7316,
    accountCode: "63711",
    accountName: "Publicidad",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-COM",
    taxTreatment: "Credito fiscal integro",
    source: "llm",
    confidence: 0.79,
    rationale:
      "Campana de performance para el cliente Nexo. Si se factura al cliente, corresponde costo de ventas y no gasto comercial.",
    pipelineRunId: "run-2026-07-0016",
    status: "pendiente",
    ingestChannel: "correo",
    sustento: "completo",

    flags: ["impacto_margen"],
  },
  {
    id: "rv-012",
    docType: "01",
    serie: "F001",
    numero: "0000992",
    issueDate: "2026-07-22",
    supplierRuc: "20521456987",
    supplierName: "Seguros Continental SA",
    currency: "PEN",
    net: 3600,
    igv: 648,
    total: 4248,
    accountCode: "65131",
    accountName: "Seguros",
    naturaleza: "gasto",
    grupoNombre: "Otros gastos de administración",
    lineaFinanzas: "G&A Expenses",
    costCenter: "CC-ADM",
    taxTreatment: "Diferido: devengo en 12 meses",
    source: "llm",
    confidence: 0.74,
    rationale:
      "Poliza anual pagada por adelantado. Statutory difiere el gasto; management lo devenga; caja lo reconoce integro en julio.",
    pipelineRunId: "run-2026-07-0016",
    status: "pendiente",
    ingestChannel: "correo",
    sustento: "completo",

    flags: ["diferido"],
  },
]

export async function fetchReviewQueue(filters: ReviewFilters = {}): Promise<ReviewItem[]> {
  if (USE_MOCKS) {
    const items = MOCK_ITEMS.filter((item) => {
      if (filters.source && filters.source !== "todas" && item.source !== filters.source) return false
      if (filters.channel && filters.channel !== "todos" && item.ingestChannel !== filters.channel)
        return false
      if (filters.maxConfidence !== undefined
          && item.confidence !== null
          && item.confidence > filters.maxConfidence) return false
      if (filters.query) {
        const q = filters.query.toLowerCase()
        const haystack = `${item.supplierName} ${item.supplierRuc} ${item.serie}-${item.numero} ${item.accountCode} ${item.accountName}`.toLowerCase()
        if (!haystack.includes(q)) return false
      }
      return true
    })
    return mockDelay(items)
  }

  // El endpoint solo acepta `entity_id` y `limite`: los cuatro filtros de la
  // pantalla no existen ahi (ver `filtrarEnCliente` en mapping.ts). Se pide el
  // maximo que admite el backend —200— y se filtra sobre eso; la cola llega
  // ordenada por confianza ascendente, asi que lo que la pantalla existe para
  // resolver siempre entra en la pagina.
  const rows = await api.get<ReviewRow[]>("/classify/review", { query: { limite: 200 } })
  return filtrarEnCliente(rows.map(reviewItemFromApi), filters)
}

export async function approveClassification(id: string, reason?: string) {
  if (USE_MOCKS) return mockDelay({ id, status: "aprobado" as const })
  const row = await api.post<ApproveRow>(`/classify/${id}/approve`, approveToApi(reason))
  return approveFromApi(row)
}

/**
 * Aprobar en lote. La respuesta NO es un ok: el backend aprueba uno por uno y
 * devuelve cuantos fallaron. Se propaga tal cual para que la pantalla pueda
 * decirlo — un 200 con 3 de 10 aprobados es el fallo silencioso que mas dano
 * hace en esta pantalla.
 */
export async function bulkApprove(ids: string[], reason: string): Promise<BulkApproveResult> {
  if (USE_MOCKS) return mockDelay({ approved: ids.length, failed: [] })
  const row = await api.post<BulkApproveRow>(
    "/classify/bulk-approve",
    bulkApproveToApi(ids, reason),
  )
  return bulkApproveFromApi(row)
}

export async function correctClassification(
  id: string,
  payload: CorrectionPayload,
  costCenters?: CostCenterLookupRow[],
) {
  if (USE_MOCKS) return mockDelay({ id, ...payload })
  const row = await api.post<CorrectRow>(
    `/classify/${id}/correct`,
    correctionToApi(payload, { costCenters }),
  )
  return correctionFromApi(row)
}

/**
 * Cuentas sugeridas para el desplegable de correccion. SOLO PARA `USE_MOCKS`.
 *
 * Estas subdivisionarias de 5 digitos NO existen en el catalogo `account_local`
 * del backend, que llega hasta la divisionaria de 3 ("639", no "63911"). Con
 * estas, toda correccion contra el backend real moria en 422. La pantalla usa
 * ahora `GET /ledger/accounts` y cae a esta lista solo cuando no hay catalogo
 * (modo mock, o el catalogo todavia cargando).
 */
export const ACCOUNT_OPTIONS = [
  { code: "60311", name: "Suministros de oficina" },
  { code: "62111", name: "Sueldos y salarios" },
  { code: "63111", name: "Transporte de carga" },
  { code: "63151", name: "Movilidad del personal" },
  { code: "63241", name: "Asesoria legal" },
  { code: "63271", name: "Asesoria empresarial" },
  { code: "63611", name: "Energia electrica" },
  { code: "63711", name: "Publicidad / representacion" },
  { code: "63911", name: "Otros servicios prestados por terceros" },
  { code: "65131", name: "Seguros" },
  { code: "65911", name: "Sanciones administrativas" },
  { code: "33611", name: "Equipos de computo (activo fijo)" },
]

/**
 * Centros de costo de ejemplo. SOLO PARA `USE_MOCKS`.
 *
 * El backend identifica el centro por UUID, no por este codigo; la pantalla usa
 * el maestro real (`GET /masters/cost-centers`) y el mapeo resuelve codigo ->
 * uuid. Si no se puede resolver, el centro no viaja (ver `correctionToApi`).
 */
export const COST_CENTER_OPTIONS = [
  { code: "CC-ADM", name: "Administracion" },
  { code: "CC-COM", name: "Comercial" },
  { code: "CC-OPE", name: "Operaciones / Delivery" },
  { code: "CC-TEC", name: "Tecnologia" },
]

/**
 * Tratamientos tributarios. SOLO PARA LA PANTALLA: `CorreccionIn` no acepta este
 * campo y el backend lo descartaria en silencio (ver `correctionToApi`). El
 * tratamiento sale de las reglas, no de la correccion de un documento.
 */
export const TAX_TREATMENT_OPTIONS = [
  "Credito fiscal integro",
  "Credito fiscal condicionado al deposito de detraccion",
  "Boleta: sin credito fiscal",
  "No deducible (art. 44 LIR) — adicion",
  "Sujeto a limite (representacion)",
  "Activo fijo: depreciacion",
  "Diferido: devengo en 12 meses",
]

/**
 * Salud del motor: que capa clasifica y cuanto se corrige a mano.
 *
 *   GET /api/classify/metrics
 *
 * No tenia pantalla: el endpoint existia desde el principio y solo se podia ver
 * con curl. Es el unico indicador de si el sistema mejora con el uso, que es la
 * promesa entera del motor de clasificacion.
 */
export async function fetchMetricas(): Promise<MetricasRow> {
  if (USE_MOCKS) {
    return mockDelay<MetricasRow>({
      por_capa: {}, total: 0, tasa_correccion_humana: 0, nota: "",
    })
  }
  return api.get<MetricasRow>("/classify/metrics")
}


/* ==========================================================================
 * Lo que llegó sin comprobante fiscal — por correo o subido a mano
 *
 *   GET /api/classify/sustento                         recuento + huérfanos
 *   GET /api/classify/sustento/{id}/archivo            el archivo tal cual llegó
 * ========================================================================== */

export interface SinComprobanteRow {
  id: string
  nombre_archivo: string
  /** Quién lo mandó y con qué asunto, o la carpeta de Drive de la que vino. */
  origen_ruta: string | null
  bytes: number
  creado_en: string | null
  mime: string | null
  canal: string | null
  sociedad: string | null
  /** `false` = la fila existe y el archivo no está en el almacén. */
  archivado: boolean
}

export async function fetchSinComprobante(entityId?: string): Promise<SinComprobanteRow[]> {
  if (USE_MOCKS) return mockDelay<SinComprobanteRow[]>([])
  const r = await api.get<{ sin_comprobante: SinComprobanteRow[] }>("/classify/sustento", {
    query: { entity_id: entityId },
  })
  return r.sin_comprobante ?? []
}

/** Pasa por `descargarArchivo`: la descarga necesita el token y la cabecera de cliente. */
export function descargarSustento(id: string, nombre: string): Promise<void> {
  return descargarArchivo(`/classify/sustento/${encodeURIComponent(id)}/archivo`, nombre)
}
